/**
 * The ticket workspace: header, thread, composer, and the sidebar with the linked order and
 * the customer card.
 *
 * Internal notes are drawn as a different object, not a different colour of the same one:
 * full width, dashed amber border, INTERNAL label. That is for the agent's eye. It is not
 * counted as a control.
 */
import type { Role, TicketMessage } from '@elutide/shared';
import { Card, Pill, Skeleton, buttonStyle } from '@/features/customers/temp-ui';
import { Composer } from './Composer';
import { RemedyPanel } from './RemedyPanel';
import { useTicket } from './api';
import { SLA_TONE, slaLabel } from './slaPill';

function Message({ message }: { message: TicketMessage }) {
  if (message.kind === 'internal_note') {
    return (
      <li
        data-kind="internal_note"
        style={{
          border: '2px dashed var(--warn)',
          background: 'var(--warn-bg)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-3)',
          margin: 'var(--space-2) 0',
        }}
      >
        <span className="caps-label" style={{ color: 'var(--warn)' }}>
          Internal · {message.author} · {message.created_at.slice(0, 16).replace('T', ' ')}
        </span>
        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{message.body}</p>
      </li>
    );
  }
  const isAgent = message.kind === 'agent_reply';
  return (
    <li
      data-kind={message.kind}
      style={{
        background: isAgent ? 'var(--surface-3)' : 'var(--surface-2)',
        borderLeft: isAgent ? '3px solid var(--burgundy)' : 'none',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-3)',
        margin: 'var(--space-2) 0',
      }}
    >
      <span className="caps-label" style={{ color: 'var(--text-faint)' }}>
        {message.author} · {message.created_at.slice(0, 16).replace('T', ' ')}
        {message.channel ? ` · ${message.channel}` : ''}
        {message.delivery_status ? ` · ${message.delivery_status}` : ''}
      </span>
      {/* Rendered as text. Links in a customer message are not clickable, on purpose. */}
      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{message.body}</p>
    </li>
  );
}

export function TicketWorkspace({ ticketRef, role }: { ticketRef: string; role: Role }) {
  const ticket = useTicket(ticketRef);

  if (ticket.isLoading) return <Skeleton shape="text" lines={10} />;
  if (ticket.isError || !ticket.data) return <Card title="Ticket">That ticket could not be loaded.</Card>;

  const data = ticket.data;
  const canSms = Boolean(data.customer_phone_last4);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 320px)' }}>
      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <Card>
          <header style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
            <h1 data-mono="true" style={{ margin: 0, fontSize: 'var(--type-heading-3)' }}>
              {data.public_ref}
            </h1>
            {data.category ? <Pill tone="warn">{data.category}</Pill> : null}
            <Pill tone={SLA_TONE[data.sla_state]}>SLA {slaLabel(data)}</Pill>
            <Pill>{data.status.replace('_', ' ')}</Pill>
            <Pill tone={data.priority === 'urgent' ? 'crit' : data.priority === 'high' ? 'warn' : 'neutral'}>
              {data.priority}
            </Pill>
          </header>
          <p style={{ color: 'var(--text-muted)', margin: 'var(--space-2) 0 0' }}>
            {data.customer_display}
            {data.is_guest ? ' · guest' : ''}
            {data.order_id ? ` · order #${data.order_id}` : ''}
            {data.order_id && !data.order_verified ? ' · unverified' : ''}
          </p>
          <h2 style={{ fontSize: 'var(--type-body)', margin: 'var(--space-2) 0 0' }}>{data.subject}</h2>
        </Card>

        <Card title="Conversation">
          <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {data.messages.map((message) => (
              <Message key={`${message.kind}-${message.message_id}`} message={message} />
            ))}
          </ol>
        </Card>

        <Composer ticketRef={ticketRef} canSms={canSms} initialDraft={data.draft?.body ?? ''} />
      </div>

      <aside style={{ display: 'grid', gap: 'var(--space-4)', alignContent: 'start' }}>
        <Card title="Linked order">
          {data.order_id ? (
            <>
              <a href={`/orders/${data.order_id}`} data-mono="true">
                #{data.order_id}
              </a>
              <p style={{ color: 'var(--text-muted)', margin: 'var(--space-2) 0 0' }}>
                {data.order_verified ? 'Contact verified against the order.' : 'Contact not verified.'}
              </p>
            </>
          ) : (
            <p style={{ color: 'var(--text-faint)', margin: 0 }}>No order linked.</p>
          )}
        </Card>

        <Card title="Customer">
          <p style={{ margin: 0 }}>{data.customer_display}</p>
          <p style={{ color: 'var(--text-muted)', margin: 'var(--space-1) 0 0' }} data-mono="true">
            {data.customer_email_masked ?? 'no email'} ·{' '}
            {data.customer_phone_last4 ? `(***) ***-${data.customer_phone_last4}` : 'no phone'}
          </p>
          {data.customer_key ? (
            <a href={`/customers/${data.customer_key}`} style={{ ...buttonStyle(), display: 'inline-block', marginTop: 'var(--space-2)', textDecoration: 'none' }}>
              Open customer
            </a>
          ) : null}
        </Card>

        {data.items.length > 0 ? (
          <Card title="Items reported">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {data.items.map((item) => (
                <li key={item.id}>
                  {item.product_name ?? item.sku ?? 'item'} x{item.qty_affected}{' '}
                  <Pill tone={item.issue_type === 'damaged' ? 'crit' : 'warn'}>{item.issue_type}</Pill>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        <RemedyPanel ticketRef={ticketRef} role={role} orderId={data.order_id} />
      </aside>
    </div>
  );
}
